import {
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { SelectedCoverage, FormState } from "../types";

import { isDuplicate } from "./utils";

interface CensusClassesSelectProps {
  options: string[];
  selectedCoverages: SelectedCoverage;
  coverageName: string;
  updateFormStateValue: <T extends keyof FormState>(
    key: T,
    value: FormState[T],
  ) => void;
}

const CensusClassesSelect = (props: CensusClassesSelectProps) => {
  const { options, coverageName, selectedCoverages, updateFormStateValue } =
    props;
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);

  const updateSelectedCoverages = (newSelectedClasses: string[]) => {
    const sortedValue = newSelectedClasses.sort(
      (a, b) => options.indexOf(a) - options.indexOf(b),
    );
    setSelectedClasses(sortedValue);

    const newSelectedCoverages = {
      ...selectedCoverages,
      [coverageName]: {
        ...selectedCoverages[coverageName],
        coverClasses: sortedValue,
      },
    };
    updateFormStateValue("selectedCoverages", newSelectedCoverages);
  };

  useEffect(() => {
    const newSelectedClasses = selectedClasses.filter((cls) =>
      options.includes(cls),
    );

    updateSelectedCoverages(newSelectedClasses);
  }, [options]);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string[];
    setSelectedClasses(value);

    updateSelectedCoverages(value);
  };

  const areAllOptionsEmpty = options.every((option) => option.trim() === "");

  return (
    <>
      <FormLabel sx={{ marginBottom: 2 }} required>
        Which classes are eligible for 100% employer paid coverage?
      </FormLabel>
      <FormControl fullWidth>
        <InputLabel id="employee-classes-label" required>
          Employee Classes
        </InputLabel>
        <Select
          labelId="employee-classes-label"
          id="employee-classes-select"
          value={selectedClasses}
          label="Employee Classes"
          multiple
          required
          onChange={handleChange}
          renderValue={(selected) =>
            (selected as string[])
              .map(
                (selectedValue) =>
                  `Class ${options.indexOf(selectedValue) + 1}`,
              )
              .join(", ")
          }
          MenuProps={{ disablePortal: true }}
        >
          {areAllOptionsEmpty ? (
            <MenuItem disabled>
              <Typography fontStyle="italic">
                No Classes added in Company Details section
              </Typography>
            </MenuItem>
          ) : (
            options.map((value, index) => (
              <MenuItem
                key={index}
                value={value}
                disabled={value === "" || isDuplicate(options, value, index)}
              >
                <Checkbox checked={selectedClasses.includes(value)} />
                <ListItemText primary={`Class ${index + 1} - ${value}`} />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </>
  );
};

export default CensusClassesSelect;
